/**
 * Message channels — SMS and WhatsApp share one Twilio number.
 *
 * The backend records which transport a message used (`channel`) and
 * keeps every phone number bare, so one person is one thread. The
 * Communication Center needs three decisions from that: how to read a
 * channel off a payload defensively, which channel a reply should go
 * out on, and what to tell the operator when a WhatsApp reply is
 * rejected because the customer's 24-hour window has closed.
 */

export type MessageChannel = 'sms' | 'whatsapp';

/** Twilio error: free-form WhatsApp message outside the 24 h window. */
export const WHATSAPP_WINDOW_ERROR = 63016;

export const readChannel = (value: unknown): MessageChannel =>
    value === 'whatsapp' ? 'whatsapp' : 'sms';

/**
 * Where a reply belongs: the channel of the person's most recent
 * inbound message. Outbound messages don't count (they are our choice,
 * not theirs), and a thread they never wrote in on is SMS.
 */
export const replyChannelFor = (
    messages: ReadonlyArray<{ direction: string; channel?: MessageChannel }>,
): MessageChannel => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
        const message = messages[i];
        if (message?.direction === 'inbound') return readChannel(message.channel);
    }
    return 'sms';
};

/** The failure line under a bubble, in words the operator can act on. */
export const describeDeliveryError = (code: number | null | undefined): string => {
    if (code === WHATSAPP_WINDOW_ERROR) return "Outside WhatsApp's 24-hour reply window";
    return code ? `Failed (${code})` : 'Failed';
};
